/**
 * Derives overlay display state (last card, last dice) from event log for history/replay.
 * Used when syncing Redux to a selected history step so the top bar shows correct last card/dice.
 */

import { EventLogEntry, EventType } from '~/server/src/dtos';
import Turfmaster from '~/server/src/tmmodel';

import type { GameStateInner } from './reducers';
import { computeStateAtEventIndex } from './reducers';

export interface ReplayOverlayDisplay {
  lastCard: number;
  lastCardHorseIndex: number;
}

/**
 * Derive last-card display for a given event index.
 * - If the state at stateEventIndex is in a dice round (turntype === DICES), clear last card.
 * - Otherwise find the last SELECTMOVEOPP event before stateEventIndex and use its carddice
 *   and turnorder[turnpos] (horse that played the card).
 */
export function getReplayOverlayDisplay(
  anchorState: GameStateInner,
  eventLog: EventLogEntry[],
  stateEventIndex: number,
  derivedState: GameStateInner | null,
): ReplayOverlayDisplay {
  const empty: ReplayOverlayDisplay = { lastCard: -1, lastCardHorseIndex: -1 };

  if (!derivedState || stateEventIndex <= 0) {
    return empty;
  }

  const turntype = derivedState.turntype as number | undefined;
  if (turntype === Turfmaster.turntypes.DICES) {
    return empty;
  }

  // Find last SELECTMOVEOPP before stateEventIndex (scan backwards)
  for (let j = stateEventIndex - 1; j >= 0; j--) {
    const entry = eventLog[j];
    if (entry?.eventType !== EventType.SELECTMOVEOPP) {
      continue;
    }
    const carddice = (entry.payload as { carddice?: number } | undefined)?.carddice;
    const stateAfterEvent = computeStateAtEventIndex(anchorState, eventLog, j + 1);
    const turnorder = stateAfterEvent.turnorder as number[] | undefined;
    const turnpos = stateAfterEvent.turnpos as number | undefined;
    const lastCardHorseIndex =
      turnorder != null && turnpos != null && turnpos < turnorder.length ? turnorder[turnpos] : -1;
    return {
      lastCard: typeof carddice === 'number' ? carddice : -1,
      lastCardHorseIndex,
    };
  }

  return empty;
}
